import { c as _c } from "react/compiler-runtime";
import { cn } from '@achra/ui/lib/utils';
type FadeDirection = 'top' | 'bottom' | 'left' | 'right' | 'radial' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
interface FadeConfig {
  /**
   * Edge, corner (for diagonals), or center (`radial`) where the gradient
   * starts at `from` opacity and travels toward the opposite side at `to`.
   */
  direction: FadeDirection;
  /** Opacity at the starting edge / corner / center. Defaults to 0. */
  from?: number;
  /** Opacity at the opposite edge / corner. Defaults to 1. */
  to?: number;
}
interface DecorationDotsProps {
  rows: number;
  columns: number;
  /** Dot diameter in px. */
  dotSize?: number;
  /** Distance between dot centers in px. */
  gap?: number;
  /** Smooth fade applied to every dot not overridden by `pattern`. */
  fade?: FadeConfig;
  /**
   * Per-cell override. `null` hides the dot, a number sets explicit opacity.
   * Cells outside the matrix bounds fall back to `fade` (or full opacity).
   */
  pattern?: ReadonlyArray<ReadonlyArray<number | null>>;
  className?: string;
}
function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
function cornerDistance(row: number, col: number, rows: number, columns: number, anchorRow: 0 | 1, anchorCol: 0 | 1): number {
  const hasMultipleRows = rows > 1;
  const hasMultipleCols = columns > 1;
  const nx = hasMultipleCols ? col / (columns - 1) : 0;
  const ny = hasMultipleRows ? row / (rows - 1) : 0;
  const dx = Math.abs(nx - anchorCol);
  const dy = Math.abs(ny - anchorRow);
  const maxDist = Math.hypot(hasMultipleCols ? 1 : 0, hasMultipleRows ? 1 : 0);
  return maxDist > 0 ? Math.hypot(dx, dy) / maxDist : 0;
}
function computeFadeOpacity(row: number, col: number, rows: number, columns: number, fade: FadeConfig): number {
  const from = fade.from ?? 0;
  const to = fade.to ?? 1;
  let t: number;
  switch (fade.direction) {
    case 'top':
      t = rows > 1 ? row / (rows - 1) : 0;
      break;
    case 'bottom':
      t = rows > 1 ? (rows - 1 - row) / (rows - 1) : 0;
      break;
    case 'left':
      t = columns > 1 ? col / (columns - 1) : 0;
      break;
    case 'right':
      t = columns > 1 ? (columns - 1 - col) / (columns - 1) : 0;
      break;
    case 'radial':
      {
        const cx = (columns - 1) / 2;
        const cy = (rows - 1) / 2;
        const maxDist = Math.hypot(cx, cy);
        t = maxDist > 0 ? Math.hypot(col - cx, row - cy) / maxDist : 0;
        break;
      }
    case 'top-left':
      t = cornerDistance(row, col, rows, columns, 0, 0);
      break;
    case 'top-right':
      t = cornerDistance(row, col, rows, columns, 0, 1);
      break;
    case 'bottom-left':
      t = cornerDistance(row, col, rows, columns, 1, 0);
      break;
    case 'bottom-right':
      t = cornerDistance(row, col, rows, columns, 1, 1);
      break;
  }
  return clamp01(from + (to - from) * t);
}

/**
 * Decorative grid of dots. Inherits its color from `currentColor` — set it
 * with semantic Tailwind utilities on `className` (e.g. `text-primary/40`).
 *
 * @example
 * <DecorationDots
 *   rows={5}
 *   columns={8}
 *   fade={{ direction: 'top', from: 0.2, to: 1 }}
 *   className="text-primary/40"
 * />
 */
function DecorationDots(t0) {
  const $ = _c(17);
  const {
    rows,
    columns,
    dotSize: t1,
    gap: t2,
    fade,
    pattern,
    className
  } = t0;
  const dotSize = t1 === undefined ? 3 : t1;
  const gap = t2 === undefined ? 16 : t2;
  const safeRows = Math.max(0, Math.floor(rows));
  const safeColumns = Math.max(0, Math.floor(columns));
  const radius = dotSize / 2;
  const width = safeColumns > 0 ? (safeColumns - 1) * gap + dotSize : 0;
  const height = safeRows > 0 ? (safeRows - 1) * gap + dotSize : 0;
  let circles;
  if ($[0] !== fade || $[1] !== gap || $[2] !== pattern || $[3] !== radius || $[4] !== safeColumns || $[5] !== safeRows) {
    circles = [] as Array<{
      key: string;
      cx: number;
      cy: number;
      r: number;
      opacity: number;
    }>;
    for (let row = 0; row < safeRows; row++) {
      const patternRow = pattern?.[row];
      for (let col = 0; col < safeColumns; col++) {
        const hasPatternEntry = patternRow !== undefined && col < patternRow.length;
        const patternValue = hasPatternEntry ? patternRow[col] : undefined;
        if (hasPatternEntry && patternValue === null) {
          continue;
        }
        let opacity;
        if (typeof patternValue === "number") {
          opacity = clamp01(patternValue);
        } else {
          if (fade) {
            opacity = computeFadeOpacity(row, col, safeRows, safeColumns, fade);
          } else {
            opacity = 1;
          }
        }
        if (opacity <= 0) {
          continue;
        }
        circles.push({
          key: `${row}-${col}`,
          cx: col * gap + radius,
          cy: row * gap + radius,
          r: radius,
          opacity
        });
      }
    }
    $[0] = fade;
    $[1] = gap;
    $[2] = pattern;
    $[3] = radius;
    $[4] = safeColumns;
    $[5] = safeRows;
    $[6] = circles;
  } else {
    circles = $[6];
  }
  if (width === 0 || height === 0 || circles.length === 0) {
    return null;
  }
  let t3;
  if ($[7] !== className) {
    t3 = cn("block shrink-0", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = `0 0 ${width} ${height}`;
  let t5;
  if ($[9] !== circles) {
    t5 = circles.map(_temp);
    $[9] = circles;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== height || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== width) {
    t6 = <svg className={t3} width={width} height={height} viewBox={t4} fill="currentColor" aria-hidden={true}>{t5}</svg>;
    $[11] = height;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = width;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp(dot) {
  return <circle key={dot.key} cx={dot.cx} cy={dot.cy} r={dot.r} opacity={dot.opacity === 1 ? undefined : dot.opacity} />;
}
export { DecorationDots };
export type { DecorationDotsProps, FadeConfig, FadeDirection };